import React, { useState } from 'react';
import {
  Box, Typography, Card, TextField, Button, Alert, Divider, Avatar, Chip
} from '@mui/material';
import { Save, AccountCircle } from '@mui/icons-material';
import { useAuth } from '../../contexts/AuthContext';
import { usersAPI } from '../../services/api';
import DevBadge from '../../components/common/DevBadge';

const PHONE_MIN = 7;
const PHONE_MAX = 15;

const ROLE_LABELS = {
  superadmin: { label: 'Super Admin', color: 'error' },
  teamlead: { label: 'Líder de Equipo', color: 'primary' },
  member: { label: 'Miembro', color: 'default' }
};

export default function Profile() {
  const { user, fetchCurrentUser } = useAuth();
  const [form, setForm] = useState({
    name: user?.name || '',
    lastName: user?.lastName || '',
    phone: user?.phone || ''
  });
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [phoneError, setPhoneError] = useState('');

  const validatePhone = (val) => {
    if (!val) return '';
    if (val.length < PHONE_MIN) return `Mínimo ${PHONE_MIN} caracteres.`;
    if (val.length > PHONE_MAX) return `Máximo ${PHONE_MAX} caracteres.`;
    return '';
  };

  const handlePhoneChange = (val) => {
    setForm(f => ({ ...f, phone: val }));
    setPhoneError(validatePhone(val));
  };

  const handleSave = async () => {
    if (!form.name.trim()) { setError('El nombre es obligatorio.'); return; }
    const pe = validatePhone(form.phone);
    if (pe) { setPhoneError(pe); return; }
    setError(''); setSuccess(''); setSaving(true);
    try {
      await usersAPI.update(user._id, {
        name: form.name.trim(),
        lastName: form.lastName.trim(),
        phone: form.phone.trim()
      });
      await fetchCurrentUser();
      setSuccess('Perfil actualizado correctamente.');
    } catch (err) {
      setError(err.response?.data?.error || 'Error al guardar. Intenta nuevamente.');
    } finally { setSaving(false); }
  };

  const initials = user?.name
    ? `${user.name[0]}${user.lastName?.[0] || ''}`.toUpperCase()
    : user?.email?.[0]?.toUpperCase() || '?';

  const roleInfo = ROLE_LABELS[user?.role] || ROLE_LABELS.member;

  return (
    <Box maxWidth={600} mx="auto">
      <DevBadge id="page:perfil" />
      <Typography variant="h5" fontWeight={700} mb={3}>Mi Perfil</Typography>
      <Card sx={{ p: { xs: 2.5, sm: 4 } }}>
        <Box display="flex" alignItems="center" gap={2} mb={3}>
          <Avatar sx={{ width: 64, height: 64, bgcolor: '#1a1a1a', fontSize: '1.4rem', fontWeight: 700 }}>
            {initials}
          </Avatar>
          <Box>
            <Typography variant="h6" fontWeight={600}>
              {user?.name ? `${user.name} ${user.lastName || ''}`.trim() : 'Usuario'}
            </Typography>
            <Typography variant="body2" color="text.secondary" mb={0.5}>{user?.email}</Typography>
            <Chip label={roleInfo.label} color={roleInfo.color} size="small" />
          </Box>
        </Box>

        <Divider sx={{ mb: 3 }} />

        {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}
        {success && <Alert severity="success" sx={{ mb: 2 }}>{success}</Alert>}

        <Box display="flex" flexDirection="column" gap={2.5}>
          <TextField
            label="Correo electrónico"
            value={user?.email || ''}
            disabled
            fullWidth
            helperText="El correo electrónico no puede ser modificado."
          />
          <Box display="flex" gap={2} flexDirection={{ xs: 'column', sm: 'row' }}>
            <TextField
              label="Nombre *"
              value={form.name}
              onChange={e => setForm(f => ({ ...f, name: e.target.value }))}
              fullWidth
            />
            <TextField
              label="Apellido"
              value={form.lastName}
              onChange={e => setForm(f => ({ ...f, lastName: e.target.value }))}
              fullWidth
            />
          </Box>
          <TextField
            label="Teléfono"
            value={form.phone}
            onChange={e => handlePhoneChange(e.target.value)}
            fullWidth
            placeholder="+58 412 000 0000"
            error={!!phoneError}
            helperText={phoneError || `Entre ${PHONE_MIN} y ${PHONE_MAX} caracteres.`}
            inputProps={{ maxLength: PHONE_MAX }}
          />
        </Box>

        <Box mt={3}>
          <Button
            variant="contained"
            startIcon={<Save />}
            onClick={handleSave}
            disabled={saving || !!phoneError}
          >
            {saving ? 'Guardando...' : 'Guardar cambios'}
          </Button>
        </Box>
      </Card>
    </Box>
  );
}
